'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Custom Radix-based Select that matches the editorial style. Use exactly
// like the native <select>: pass items as <SelectItem value>...</SelectItem>.
//
// Example:
//   <Select value={priority} onValueChange={setPriority} placeholder="Priority">
//     <SelectItem value="urgent">Urgent</SelectItem>
//     <SelectItem value="high">High</SelectItem>
//   </Select>

interface SelectProps {
  value: string;
  onValueChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  triggerClassName?: string;
  /** Use mono font + tabular numerics on the trigger label. */
  mono?: boolean;
  children: ReactNode;
  disabled?: boolean;
}

export function Select({
  value,
  onValueChange,
  placeholder,
  ariaLabel,
  triggerClassName,
  mono,
  children,
  disabled,
}: SelectProps) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface-sunken/60 px-3 py-2 text-sm outline-none transition duration-fast ease-out-quint hover:border-border-strong focus:border-accent disabled:cursor-not-allowed disabled:opacity-50',
          mono && 'font-mono tabular-nums',
          triggerClassName,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-3.5 w-3.5 text-subtle-foreground" strokeWidth={2} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-hairline bg-surface/70 p-1 shadow-lg backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out data-[state=open]:fade-in',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <SelectPrimitive.Viewport className="max-h-[18rem]">{children}</SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

interface SelectItemProps {
  value: string;
  children: ReactNode;
  /** Use mono on the item label (for time/number items). */
  mono?: boolean;
}

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({ value, children, mono }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      value={value}
      className={cn(
        // Text on the left, indicator on the right (justify-between). The
        // indicator slot is reserved at all times so item rows stay the
        // same height/width whether or not they're the selected value.
        'relative flex cursor-pointer select-none items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 pr-2 text-sm outline-none transition duration-fast ease-out-quint',
        'data-[highlighted]:bg-surface-muted data-[state=checked]:font-medium',
        mono && 'font-mono tabular-nums',
      )}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = 'SelectItem';
